import React, { useState, useEffect } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { useApi } from "../hooks/useApi";
import { format } from "date-fns";

const card = { background: "#0d1224", border: "1px solid #1e2a45", borderRadius: "10px", padding: "14px" };

export default function EquityCurve() {
  const { data: histData } = useApi("/equity_history?limit=200", 60000);
  const { data: posData }  = useApi("/positions", 10000);
  const [points, setPoints] = useState([]);

  // Append live equity from /positions every poll
  useEffect(() => {
    const acc = posData?.account;
    if (acc?.equity) {
      setPoints((prev) => {
        const pt = {
          time:    format(new Date(), "HH:mm"),
          equity:  acc.equity,
          balance: acc.balance,
        };
        const last = prev[prev.length - 1];
        if (last && Math.abs(last.equity - acc.equity) < 0.01) return prev;
        return [...prev, pt].slice(-300);
      });
    }
  }, [posData]);

  // Merge historical + live
  const historical = (histData?.snapshots || []).map((s) => ({
    time:    format(new Date(s.timestamp), "HH:mm"),
    equity:  s.equity,
    balance: s.balance,
  }));
  const chartData = historical.length > 0 ? [...historical, ...points].slice(-300) : points;

  const startBal = chartData[0]?.balance || 0;
  const curEquity = chartData[chartData.length - 1]?.equity || 0;
  const pnl = curEquity - startBal;

  return (
    <div style={card}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
        <span style={{ fontWeight: 600, color: "#94a3b8", fontSize: "13px" }}>📈 Equity Curve</span>
        <div style={{ fontSize: "11px", display: "flex", gap: "14px" }}>
          {curEquity > 0 && (
            <>
              <span style={{ color: "#60a5fa" }}>${curEquity.toFixed(2)}</span>
              <span style={{ color: pnl >= 0 ? "#4ade80" : "#f87171" }}>
                {pnl >= 0 ? "+" : ""}${pnl.toFixed(2)}
              </span>
            </>
          )}
          <span style={{ color: "#334155" }}>{chartData.length} pts</span>
        </div>
      </div>

      {chartData.length < 2 ? (
        <div style={{ color: "#334155", textAlign: "center", padding: "50px 0", fontSize: "12px" }}>
          {curEquity > 0 ? `Equity: $${curEquity.toFixed(2)} — collecting data points...` : "Connecting to MT5..."}
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={180}>
          <LineChart data={chartData}>
            <XAxis dataKey="time" tick={{ fill: "#475569", fontSize: 9 }} interval="preserveStartEnd" />
            <YAxis tick={{ fill: "#475569", fontSize: 9 }} domain={["auto", "auto"]}
                   tickFormatter={(v) => `$${(v/1000).toFixed(1)}k`} width={55} />
            <Tooltip contentStyle={{ background: "#1e2a45", border: "1px solid #334155", borderRadius: "6px", fontSize: "11px" }}
                     formatter={(v) => [`$${v.toFixed(2)}`, ""]} />
            {startBal > 0 && <ReferenceLine y={startBal} stroke="#334155" strokeDasharray="4 4" />}
            <Line type="monotone" dataKey="equity" stroke="#60a5fa" dot={false} strokeWidth={2} isAnimationActive={false} />
            <Line type="monotone" dataKey="balance" stroke="#475569" dot={false} strokeWidth={1} strokeDasharray="4 4" isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
